import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { Sparkles } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
      <div className="grid lg:grid-cols-2 gap-16 max-w-6xl w-full items-center my-auto">
        <div className="hidden lg:block">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
              <Sparkles size={18} strokeWidth={1.6} />
            </div>
            <div className="font-serif text-2xl text-ink">Wisha</div>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
            Eine Liste,<br />
            <span className="italic text-sage">die euch verbindet</span>
          </h1>
          <p className="text-text_secondary text-lg mt-8 max-w-md leading-relaxed">
            Wisha ist euer privater Raum. Ihr beide. Eure Wünsche.
            Eure Geheimnisse. Eure Geschichten.
          </p>
        </div>

        <div className="bg-white border border-border rounded-3xl p-8 sm:p-12">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
              <Sparkles size={18} strokeWidth={1.6} />
            </div>
            <div className="font-serif text-2xl text-ink">Wisha</div>
          </div>

          <h2 className="font-serif text-3xl text-ink mb-2">Account erstellen</h2>
          <p className="text-text_secondary mb-8">Beginnt euer gemeinsames Kapitel.</p>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Dein Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="z.B. Anna"
                className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Passwort (min. 6 Zeichen)</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink"
                data-testid="register-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-ink text-alabaster hover:bg-ink/90 py-6 text-base"
              data-testid="register-submit-button"
            >
              {loading ? "Account erstellen …" : "Account erstellen"}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-sm text-text_secondary">Schon dabei? </p>
            <Link to="/login" className="text-ink font-medium hover:underline" data-testid="link-to-login">
              Jetzt anmelden →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
